import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const inputCls = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-dwc-charcoal outline-none focus:border-dwc-green";
const LANGS = { en: "English", hi: "Hindi", gu: "Gujarati" };
const EMPTY = { qnum: "", language: "en", question: "", image: "", o0: "", o1: "", o2: "", o3: "", correct_index: 0, category: "", explanation: "", active: true };

const QModal = ({ q, token, onClose, onSaved }) => {
  const [f, setF] = useState(q ? { ...EMPTY, ...q, o0: q.options[0], o1: q.options[1], o2: q.options[2], o3: q.options[3] } : EMPTY);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    const payload = {
      qnum: f.qnum ? parseInt(f.qnum, 10) : null, language: f.language, question: f.question, image: f.image,
      options: [f.o0, f.o1, f.o2, f.o3], correct_index: parseInt(f.correct_index, 10),
      category: f.category, explanation: f.explanation, active: f.active,
    };
    try {
      if (q?.id) await axios.put(`${API}/api/ll/admin/questions/${q.id}`, payload, auth(token));
      else await axios.post(`${API}/api/ll/admin/questions`, payload, auth(token));
      toast.success("Question saved");
      onSaved();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-center overflow-y-auto bg-dwc-charcoal/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={save} className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" data-testid="ll-question-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-extrabold text-dwc-charcoal">{q ? "Edit Question" : "Add Question"}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-2 text-dwc-charcoal/50 hover:bg-dwc-soft"><X className="h-5 w-5" aria-hidden="true" /></button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <select value={f.language} onChange={set("language")} data-testid="llq-language" className={inputCls}>
            {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="number" value={f.qnum || ""} onChange={set("qnum")} placeholder="Source Q. No." className={inputCls} />
          <input value={f.category} onChange={set("category")} placeholder="Category" className={inputCls} />
          <textarea required rows={2} value={f.question} onChange={set("question")} placeholder="Question text *" data-testid="llq-question" className={`${inputCls} sm:col-span-3`} />
          <input value={f.image} onChange={set("image")} placeholder="Image URL (for sign questions)" className={`${inputCls} sm:col-span-3`} />
          {[0, 1, 2, 3].map((i) => (
            <input key={i} required value={f[`o${i}`]} onChange={set(`o${i}`)} placeholder={`Option ${i + 1} *`} data-testid={`llq-option-${i}`} className={`${inputCls} ${i < 2 ? "" : ""} sm:col-span-3`} />
          ))}
          <select value={f.correct_index} onChange={set("correct_index")} data-testid="llq-correct" className={inputCls}>
            {[0, 1, 2, 3].map((i) => <option key={i} value={i}>Correct: Option {i + 1}</option>)}
          </select>
          <input value={f.explanation} onChange={set("explanation")} placeholder="Explanation (optional)" className={`${inputCls} sm:col-span-2`} />
          <label className="flex items-center gap-2 text-xs font-bold text-dwc-charcoal/70 sm:col-span-3">
            <input type="checkbox" checked={f.active} onChange={set("active")} className="accent-dwc-green" /> Active
          </label>
        </div>
        <button type="submit" data-testid="llq-save-btn" className="mt-4 w-full rounded-xl bg-dwc-green py-3.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-dwc-green-dark">Save Question</button>
      </form>
    </div>
  );
};

export const LLAdmin = ({ token }) => {
  const [lang, setLang] = useState("en");
  const [questions, setQuestions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    const [q, s, a] = await Promise.all([
      axios.get(`${API}/api/ll/admin/questions?language=${lang}`, auth(token)),
      axios.get(`${API}/api/ll/settings`),
      axios.get(`${API}/api/ll/admin/attempts`, auth(token)),
    ]);
    setQuestions(q.data); setSettings(s.data); setAttempts(a.data);
  }, [token, lang]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const del = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    await axios.delete(`${API}/api/ll/admin/questions/${q.id}`, auth(token));
    load();
  };
  const saveSettings = async (e) => {
    e.preventDefault();
    await axios.put(`${API}/api/ll/admin/settings`, {
      questions_per_attempt: parseInt(settings.questions_per_attempt, 10),
      seconds_per_question: parseInt(settings.seconds_per_question, 10),
      pass_score: parseInt(settings.pass_score, 10),
      randomize: true,
    }, auth(token));
    toast.success("Exam settings saved");
  };

  return (
    <div className="mt-6 space-y-6" data-testid="ll-admin-panel">
      {settings && (
        <form onSubmit={saveSettings} className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-100 bg-white p-5" data-testid="ll-settings-form">
          {[["questions_per_attempt", "Questions / Attempt"], ["seconds_per_question", "Seconds / Question"], ["pass_score", "Passing Score"]].map(([k, label]) => (
            <label key={k} className="text-[11px] font-extrabold uppercase tracking-wide text-dwc-charcoal/60">
              {label}
              <input type="number" min="1" value={settings[k]} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} data-testid={`ll-setting-${k}`} className={`${inputCls} mt-1 w-32`} />
            </label>
          ))}
          <button type="submit" data-testid="ll-settings-save" className="rounded-xl bg-dwc-green px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white">Save Exam Settings</button>
        </form>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(LANGS).map(([k, v]) => (
          <button key={k} onClick={() => setLang(k)} data-testid={`ll-admin-lang-${k}`} className={`rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-wide ${lang === k ? "bg-dwc-green text-white" : "border border-dwc-green/25 bg-white text-dwc-green"}`}>
            {v}
          </button>
        ))}
        <span className="text-xs font-bold text-dwc-charcoal/50">{questions.length} question{questions.length === 1 ? "" : "s"}</span>
        <button onClick={() => setModal("new")} data-testid="ll-add-question-btn" className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-dwc-green px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white">
          <Plus className="h-4 w-4" aria-hidden="true" /> Add Question
        </button>
      </div>
      {questions.length === 0 ? (
        <p className="rounded-2xl bg-white p-8 text-center text-sm font-semibold text-dwc-charcoal/55" data-testid="ll-no-questions">
          No {LANGS[lang]} questions yet. Add questions manually or ask the DWC team to import the official Transport Department question bank (bulk import API available).
        </p>
      ) : (
        <div className="space-y-2">
          {questions.map((q) => (
            <div key={q.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4" data-testid={`ll-question-row-${q.id}`}>
              <span className="text-xs font-black text-dwc-green">{q.qnum || "—"}</span>
              <p className="min-w-0 flex-1 truncate text-sm font-bold text-dwc-charcoal">{q.question}</p>
              {!q.active && <span className="rounded bg-gray-100 px-2 py-0.5 text-[9px] font-black uppercase text-gray-500">Inactive</span>}
              <button onClick={() => setModal(q)} aria-label="Edit question" className="rounded-lg p-2 text-dwc-green hover:bg-dwc-soft"><Pencil className="h-4 w-4" aria-hidden="true" /></button>
              <button onClick={() => del(q)} aria-label="Delete question" className="rounded-lg p-2 text-dwc-crimson hover:bg-dwc-crimson/5"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-2xl border border-gray-100 bg-white p-5" data-testid="ll-attempts-admin">
        <h3 className="text-xs font-extrabold uppercase tracking-wide text-dwc-charcoal/50">Recent Exam Attempts ({attempts.length})</h3>
        <div className="mt-3 space-y-1.5">
          {attempts.slice(0, 10).map((a) => (
            <p key={a.id} className="flex flex-wrap justify-between gap-2 text-[13px] font-bold text-dwc-charcoal">
              <span>{a.mobile} · {LANGS[a.language]} · #{a.attempt_no}</span>
              <span className={a.passed ? "text-dwc-green" : "text-dwc-crimson"}>{a.score}/{a.total} {a.passed ? "PASS" : "FAIL"}</span>
            </p>
          ))}
          {attempts.length === 0 && <p className="text-sm text-dwc-charcoal/50">No attempts yet.</p>}
        </div>
      </div>
      {modal && <QModal q={modal === "new" ? null : modal} token={token} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
};
